import type { AcceptInvitationInput, PublicInvitation } from '@nolens/contracts';
import { Banner, Button, Checkbox, EmptyState, Field, Input, LoadingState } from '@nolens/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { NolensMark, PoweredByNolens } from '../app/Brand';
import { api, ApiError } from '../lib/api';
import { DEV_LOGIN_ENABLED } from '../lib/devAuth';
import { keys } from '../lib/queries';
import { signOut, useSession } from '../lib/session';
import { auth } from '../lib/supabase';

type Step = 'welcome' | 'code' | 'consent';

/**
 * Onboarding (docs/user-flows.md F4): convite → código por email → condições → plano.
 * Ativação = primeiro exercício, por isso o fluxo é curto e termina no "Hoje".
 */
export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { session } = useSession();
  const inv = useQuery({ queryKey: ['invitation', token], queryFn: () => api<PublicInvitation>(`/v1/public/invitations/${token}`), retry: false });
  const [stepState, setStep] = useState<Step>('welcome');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  // Equipa: o nome aparece aos colegas e aos utentes ("Inês viu o seu check-in"); o utente já vem com nome da ficha.
  const [name, setName] = useState('');
  const [consents, setConsents] = useState({ terms: false, privacyNotice: false, healthData: false, emailNotifications: true });

  const data = inv.data;
  const sessionEmail = session?.user.email?.toLowerCase();
  const matches = !!data && sessionEmail === data.email.toLowerCase();
  // Já com sessão no email certo: segue diretamente para as condições.
  const step: Step = matches ? 'consent' : stepState;

  useEffect(() => {
    if (!data) return;
    const root = document.documentElement;
    root.style.setProperty('--accent', data.branding.primaryColor);
    root.style.setProperty('--on-accent', data.branding.onPrimaryColor);
  }, [data]);


  if (inv.isPending) return <LoadingState />;
  if (inv.isError || !data) {
    return <EmptyState title="Convite inválido" description={inv.error instanceof ApiError ? inv.error.message : 'Peça um novo convite à sua clínica.'} />;
  }
  if (data.status !== 'pending') {
    return (
      <EmptyState
        title={data.status === 'accepted' ? 'Este convite já foi usado' : 'Este convite já não está ativo'}
        description={data.status === 'accepted' ? 'Entre com o seu email para continuar.' : 'Peça um novo convite à sua clínica.'}
        action={data.status === 'accepted' && <Button onClick={() => void navigate('/entrar')}>Entrar</Button>}
      />
    );
  }

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Não foi possível continuar. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  const start = () =>
    run(async () => {
      if (session && !matches) await signOut();
      await api(`/v1/public/invitations/${token}/start`, { method: 'POST' });
      if (!auth || DEV_LOGIN_ENABLED) {
        setError('Em desenvolvimento sem Supabase, o código por email não está disponível.');
        return;
      }
      const { error: otpError } = await auth.signInWithOtp({ email: data.email, options: { shouldCreateUser: false } });
      if (otpError) throw new Error(otpError.message);
      setStep('code');
    });

  const verify = () =>
    run(async () => {
      const { error: vErr } = await auth!.verifyOtp({ email: data.email, token: code.trim(), type: 'email' });
      if (vErr) throw new ApiError(400, 'VALIDATION_FAILED', 'O código não é válido ou expirou. Peça um novo código.');
      setStep('consent');
    });

  const accept = () =>
    run(async () => {
      const body: AcceptInvitationInput = {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(data.kind === 'patient' && { consents: consents as AcceptInvitationInput['consents'] }),
        ...(data.kind !== 'patient' && { displayName: name.trim() }),
      };
      const r = await api<{ organizationId: string; surface: 'patient' | 'clinic' }>(`/v1/invitations/${token}/accept`, { method: 'POST', body });
      await qc.invalidateQueries({ queryKey: keys.me });
      void navigate(r.surface === 'patient' ? `/p/${r.organizationId}/hoje` : `/c/${r.organizationId}`, { replace: true });
    });

  const canAccept = data.kind === 'staff' ? name.trim().length >= 2 : (consents.terms && consents.privacyNotice && consents.healthData);

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-5 py-10">
      <p className="text-[15px] font-semibold">{data.branding.displayName}</p>
      <div className="flex flex-1 flex-col justify-center gap-6 py-10">
        {step === 'welcome' && (
          <>
            <h1 className="text-[30px] font-semibold leading-tight tracking-tight">
              {data.firstName ? `Olá, ${data.firstName}.` : 'Olá.'}
            </h1>
            <p className="text-[17px] leading-relaxed text-text-2">
              {data.kind === 'patient'
                ? `A ${data.branding.displayName} preparou um acompanhamento para continuar a sua fisioterapia em casa: o plano de exercícios, os vídeos e uma forma simples de dizer como correu.`
                : `Foi convidado(a) para a equipa da ${data.branding.displayName}.`}
            </p>
            {data.branding.onboardingText && <Banner>{data.branding.onboardingText}</Banner>}
            <p className="text-sm text-text-3">Vamos enviar um código para {data.email}. Não precisa de palavra-passe.</p>
            <Button size="lg" fullWidth loading={busy} onClick={() => void start()}>
              Continuar
            </Button>
          </>
        )}
        {step === 'code' && (
          <form
            className="flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void verify();
            }}
          >
            <h1 className="text-[26px] font-semibold tracking-tight">Verifique o seu email</h1>
            <p className="text-text-2">Enviámos um código de 6 dígitos para {data.email}.</p>
            <Field label="Código">
              {(p) => (
                <Input {...p} inputMode="numeric" autoComplete="one-time-code" maxLength={6} required className="text-center text-xl tracking-[0.4em]" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              )}
            </Field>
            <Button type="submit" size="lg" fullWidth loading={busy}>
              Confirmar
            </Button>
          </form>
        )}
        {step === 'consent' && (
          <>
            <h1 className="text-[26px] font-semibold tracking-tight">{data.kind === 'patient' ? 'Antes de começar' : 'Juntar-se à equipa'}</h1>
            {data.kind === 'patient' ? (
              <div className="flex flex-col gap-4">
                <Checkbox checked={consents.terms} onChange={(e) => setConsents({ ...consents, terms: e.target.checked })} label="Aceito os termos de utilização." />
                <Checkbox checked={consents.privacyNotice} onChange={(e) => setConsents({ ...consents, privacyNotice: e.target.checked })} label="Li a política de privacidade." />
                <Checkbox
                  checked={consents.healthData}
                  onChange={(e) => setConsents({ ...consents, healthData: e.target.checked })}
                  label={`Autorizo a ${data.branding.displayName} a tratar os dados de saúde que eu registar aqui, para o meu acompanhamento.`}
                />
                <Checkbox checked={consents.emailNotifications} onChange={(e) => setConsents({ ...consents, emailNotifications: e.target.checked })} label="Quero receber avisos por email (sem informação clínica)." description="Pode mudar isto a qualquer momento." />
                <p className="text-xs text-text-3">Este canal não substitui uma consulta nem serve para urgências.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="text-text-2">Vai ter acesso à área clínica da {data.branding.displayName}.</p>
                <Field label="O seu nome" hint="É o nome que os colegas e os utentes veem.">
                  {(p) => <Input {...p} autoComplete="name" required minLength={2} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />}
                </Field>
              </div>
            )}
            <Button size="lg" fullWidth loading={busy} disabled={!canAccept} onClick={() => void accept()}>
              {data.kind === 'patient' ? 'Ver o meu plano' : 'Entrar na equipa'}
            </Button>
          </>
        )}
        {error && <p className="text-critical">{error}</p>}
      </div>
      <div className="flex items-center justify-between">
        <NolensMark size="sm" />
        <PoweredByNolens />
      </div>
    </main>
  );
}
